import { RefObject, useEffect } from "react";

import { getNearestScrollableParent, requestIdleCallback } from "@utils/misc.util";

/**
 * Custom hook that enables/disables scroll lock based on the provided `enabled` flag.
 *
 * When scroll lock is enabled, the user is prevented from scrolling the page while a modal is open.
 * This is achieved by preventing the default behavior of the `touchmove` event on the document element.
 * Additionally, the hook ensures that the page remains fixed at the top when the user focuses on an input field.
 *
 * Note: This hook is intended to be used in conjunction with a modal component.
 *
 * @param {Object} options - The options object.
 * @param {boolean} options.enabled - Flag indicating whether scroll lock should be enabled.
 * @param {Object} [options.containerRef] - Element whose scrolling ancestor is frozen too, for
 * overlays anchored inside a scrolling panel rather than over the page.
 * @returns {void}
 */
export function useScrollLock({
  enabled,
  containerRef,
}: {
  enabled: boolean;
  containerRef?: RefObject<HTMLElement>;
}) {
  useEffect(() => {
    if (!enabled) return;

    const handleTouchMove = (e: TouchEvent) => {
      const scrollableParent = getNearestScrollableParent(e.target as HTMLElement);

      // Prevent scrolling the page when a modal is open and the user tries to scroll the window.
      if (scrollableParent === document.documentElement) {
        e.preventDefault();
        e.stopImmediatePropagation();

        return false;
      }

      return true;
    };

    window.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      window.removeEventListener("touchmove", handleTouchMove);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;

    function loop() {
      const offsetTop = window.scrollY || visualViewport?.pageTop || 0;
      const scrollY = Math.abs(offsetTop) * -1;

      if (offsetTop !== 0) {
        window.scrollBy(0, scrollY);
        requestIdleCallback(loop);
      }
    }

    const handleFocus = () => requestIdleCallback(loop);

    window.addEventListener("focusin", handleFocus);
    window.visualViewport?.addEventListener("resize", handleFocus);
    window.visualViewport?.addEventListener("scroll", handleFocus);

    return () => {
      window.removeEventListener("focusin", handleFocus);
      window.visualViewport?.removeEventListener("resize", handleFocus);
      window.visualViewport?.removeEventListener("scroll", handleFocus);
    };
  }, [enabled]);

  // An open dropdown is positioned against its field, so scrolling the panel underneath it would
  // leave the two apart. Freeze the panel until it closes.
  useEffect(() => {
    const container = containerRef?.current;
    const scrollable = enabled && container ? getNearestScrollableParent(container) : null;
    if (!scrollable) return;

    const previousOverflow = scrollable.style.overflowY;
    scrollable.style.overflowY = "hidden";

    return () => {
      scrollable.style.overflowY = previousOverflow;
    };
  }, [enabled, containerRef]);
}
